import { useMemo, useRef, useState, type ReactNode } from "react";
import classNames from "classnames";
import { Avatar } from "../Avatar";
import { useUsers } from "../../hooks/useDirectory";
import { useParties } from "../../hooks/useDirectory";
import { displayName } from "../../api/tawa";

export interface TypeaheadItem {
  key: string;
  label: string;
  /** Rendu custom d'une entrée (avatar, badge…). */
  render?: ReactNode;
}

interface TypeaheadProps {
  items: TypeaheadItem[];
  placeholder?: string;
  onPick: (item: TypeaheadItem) => void;
  /** Vide l'input après sélection (défaut true). */
  clearOnPick?: boolean;
}

/** Input + liste filtrée navigable au clavier (↑ ↓ ⏎ Escape). */
export function Typeahead({
  items,
  placeholder,
  onPick,
  clearOnPick = true,
}: TypeaheadProps) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items.slice(0, 8);
    return items
      .filter((i) => i.label.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [items, q]);

  const pick = (item: TypeaheadItem) => {
    onPick(item);
    if (clearOnPick) setQ("");
    setOpen(false);
    setCursor(0);
  };

  return (
    <div className="typeahead">
      <input
        ref={inputRef}
        className="typeahead-input"
        value={q}
        placeholder={placeholder}
        aria-expanded={open}
        role="combobox"
        aria-autocomplete="list"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setCursor(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const item = filtered[cursor];
            if (item) pick(item);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && filtered.length > 0 && (
        <div className="typeahead-list" role="listbox">
          {filtered.map((item, i) => (
            <button
              key={item.key}
              type="button"
              role="option"
              aria-selected={i === cursor}
              className={classNames("typeahead-item", { active: i === cursor })}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
              onMouseEnter={() => setCursor(i)}
            >
              {item.render ?? item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface UserPickerProps {
  placeholder?: string;
  onPick: (v: { ref: string; name: string; isContact: boolean }) => void;
  /** Refs déjà sélectionnées, exclues de la liste. */
  exclude?: string[];
  /** Inclut les contacts CRM (acteurs `contact`, actor_ref = crm:…). */
  withContacts?: boolean;
}

/** Typeahead spécialisé annuaire : membres TAWA + contacts CRM (badge). */
export function UserPicker({
  placeholder = "Ajouter…",
  onPick,
  exclude = [],
  withContacts = false,
}: UserPickerProps) {
  const users = useUsers();
  const parties = useParties(undefined);

  const items = useMemo<TypeaheadItem[]>(() => {
    const excl = new Set(exclude);
    const out: TypeaheadItem[] = [];
    for (const u of users.data ?? []) {
      const ref = u.worker_url || u.username;
      if (excl.has(ref)) continue;
      const name = displayName(u);
      out.push({
        key: ref,
        label: name,
        render: (
          <span className="typeahead-user">
            <Avatar name={name} size={20} />
            <span>{name}</span>
          </span>
        ),
      });
    }
    if (withContacts) {
      for (const p of parties.data ?? []) {
        const ref = `crm:${p.id}`;
        if (excl.has(ref)) continue;
        out.push({
          key: ref,
          label: p.display_name,
          render: (
            <span className="typeahead-user">
              <Avatar name={p.display_name} size={20} />
              <span>{p.display_name}</span>
              <span
                className="chip"
                style={{
                  color: "var(--violet)",
                  background: "var(--violet-soft)",
                }}
              >
                contact
              </span>
            </span>
          ),
        });
      }
    }
    return out;
  }, [users.data, parties.data, exclude, withContacts]);

  return (
    <Typeahead
      items={items}
      placeholder={placeholder}
      onPick={(item) =>
        onPick({
          ref: item.key,
          name: item.label,
          isContact: item.key.startsWith("crm:"),
        })
      }
    />
  );
}
